import { BaseProvider } from "./base.provider";

export type CreditOp = "add" | "set" | "floor";

export interface CreditAdjustment {
  op: CreditOp;
  amount: number;
  /** 1–500 chars; the API rejects the adjustment without it. */
  note: string;
}

export type CreditLedgerReason =
  | "signup"
  | "store_purchase"
  | "event_create"
  | "event_boost"
  | "admin_adjust";

export interface CreditLedgerEntry {
  credit_ledger_id: string;
  user_id: string;
  /** Positive for credits added, negative for credits spent or removed. */
  delta: number;
  balance_after: number;
  reason: CreditLedgerReason;
  /** Referrer for signup, event for event_*, club for a club-wide admin_adjust. */
  ref_id: string | null;
  actor_user_id: string | null;
  note: string | null;
  created_at: string;
}

export interface CreditLedgerPage {
  data: CreditLedgerEntry[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface UserCreditsResponse {
  user_id: string;
  credits: number;
}

export interface ClubCreditsResponse {
  club_id: string;
  /** Balances that actually changed, not club size — members already at the target aren't counted. */
  users_updated: number;
}

export interface AllUsersCreditsResponse {
  users_updated: number;
}

class CreditsProvider extends BaseProvider {
  adjustUser(
    userId: string,
    body: CreditAdjustment,
  ): Promise<UserCreditsResponse> {
    return this.request<UserCreditsResponse>(
      `/v1/admin/users/${encodeURIComponent(userId)}/credits`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  // The club route answers 404 when zero rows change, which is indistinguishable
  // from the club not existing — and with `floor`, "everyone already qualifies"
  // is a normal outcome. Callers pick the club from a list, so report it as
  // zero updated rather than as a missing club.
  async adjustClub(
    clubId: string,
    body: CreditAdjustment,
  ): Promise<ClubCreditsResponse> {
    const result = await this.requestOrNullOn404<ClubCreditsResponse>(
      `/v1/admin/clubs/${encodeURIComponent(clubId)}/credits`,
      { method: "POST", body: JSON.stringify(body) },
    );
    return result ?? { club_id: clubId, users_updated: 0 };
  }

  adjustAllUsers(body: CreditAdjustment): Promise<AllUsersCreditsResponse> {
    return this.request<AllUsersCreditsResponse>(`/v1/admin/credits`, {
      method: "POST",
      body: JSON.stringify({ ...body, confirm: "ALL_USERS" }),
    });
  }

  getUserLedger(userId: string, page = 1): Promise<CreditLedgerPage> {
    return this.request<CreditLedgerPage>(
      `/v1/admin/users/${encodeURIComponent(userId)}/credits/ledger?page=${page}`,
    );
  }
}

export const creditsProvider = new CreditsProvider();
